import React from 'react';
import type { PaymentScheduleBreakdown as Breakdown } from '../../utils/paymentSchedule';
import { installmentBillingFigures } from '../../utils/portalStatement';
import './PaymentScheduleBreakdown.css';

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

export interface PaymentRowStatus {
  status: 'not_billed' | 'open' | 'partial' | 'paid' | 'voided' | string;
  paidAt?: Date;
  amountBilled?: number;
  amountPaid?: number;
  /** Ignored for display — recomputed from the live schedule amount. */
  amountUnbilled?: number;
  partiallyBilled?: boolean;
  structureGroupLabels?: string[];
}

export interface PaymentScheduleBreakdownProps {
  breakdown: Breakdown;
  /** 'dark' matches the public proposal theme; 'light' for portal / builder. */
  variant?: 'dark' | 'light';
  /** Compact rows for sidebars/banners. */
  compact?: boolean;
  /** Optional per-row paid status keyed by row id (deposit/pre_shipment/final/co-<id>). */
  statuses?: Record<string, PaymentRowStatus>;
}

const formatStatusDate = (d?: Date): string =>
  d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const renderStatusBadge = (s?: PaymentRowStatus): React.ReactNode => {
  if (!s || s.status === 'not_billed') return null;
  if (s.status === 'paid') {
    return (
      <span className="psb-badge psb-badge--paid">
        Paid{s.paidAt ? ` · ${formatStatusDate(s.paidAt)}` : ''}
      </span>
    );
  }
  if (s.status === 'partial') {
    return <span className="psb-badge psb-badge--partial">Partially paid</span>;
  }
  if (s.status === 'voided' || s.status === 'removed_in_qb') return null;
  const billed = s.amountBilled ?? 0;
  const partly = s.partiallyBilled || (billed > 0 && (s.amountUnbilled ?? 0) > 0);
  if (partly) {
    return <span className="psb-badge psb-badge--invoiced">Partial</span>;
  }
  return <span className="psb-badge psb-badge--invoiced">Invoiced</span>;
};

/**
 * Renders the contract payment schedule: base installments, folded change-order
 * adjustments (with per-CO detail), immediately billed CO lines, and the
 * revised contract summary.
 */
const PaymentScheduleBreakdown: React.FC<PaymentScheduleBreakdownProps> = ({
  breakdown,
  variant = 'dark',
  compact = false,
  statuses,
}) => {
  const { rows, baseTotal, currentContractValue, hasChangeOrders, refundDue } = breakdown;
  const coTotal = breakdown.foldedCoTotal + breakdown.immediateCoTotal;

  return (
    <div className={`psb psb--${variant} ${compact ? 'psb--compact' : ''}`}>
      <div className="psb-rows">
        {rows.map(row => {
          const status = statuses?.[row.id];
          const billed = status?.amountBilled;
          const partly = !!status && billed != null && (
            status.partiallyBilled || (billed > 0 && billed < row.amount)
          );
          // Always derive not-yet-billed from the live schedule row amount so
          // later signed COs folded into this installment raise the figure even
          // when the portal mirror still has a stale amountUnbilled.
          const figures = installmentBillingFigures({
            amount: row.amount,
            amountBilled: billed,
            amountPaid: status?.amountPaid,
          });
          const paid = figures.amountPaid;
          const invoiced = figures.amountBilled;
          const dueNow = figures.dueNow;
          const notYetBilled = figures.amountUnbilled;
          const groups = (status?.structureGroupLabels || []).filter(Boolean);
          return (
          <div key={row.id} className={`psb-row ${row.kind === 'change_order' ? 'psb-row--co' : ''}`}>
            <div className="psb-row-main">
              <span className="psb-row-label">{row.label}</span>
              <span className="psb-row-detail">{row.detail}</span>
              {statuses && renderStatusBadge(status)}
              {partly ? (
                <span className="psb-row-figures">
                  <span className="psb-row-amount">{fmt(row.amount)}</span>
                  <span className="psb-due-caption">
                    {fmt(dueNow)} {dueNow > 0 ? 'due now' : 'paid'}
                  </span>
                </span>
              ) : (
                <span className="psb-row-amount">{fmt(row.amount)}</span>
              )}
            </div>
            {row.coAdjustments.length > 0 && (
              <div className="psb-row-adjustments">
                <span className="psb-adj-base">Base {fmt(row.baseAmount)}</span>
                {row.coAdjustments.map((adj, i) => (
                  <span
                    key={`${adj.changeOrderId || adj.reference}-${i}`}
                    className={`psb-adj ${adj.amount < 0 ? 'psb-adj--credit' : ''}`}
                    title={adj.title}
                  >
                    {adj.amount >= 0 ? '+' : '−'}{fmt(Math.abs(adj.amount))} ({adj.reference})
                  </span>
                ))}
              </div>
            )}
            {partly && (
              <div className="psb-row-adjustments">
                <span className="psb-adj-base">
                  {groups.length ? `${groups.join(', ')} · ` : ''}
                  Invoiced {fmt(invoiced)}
                  {' · '}
                  Paid {fmt(paid)}
                </span>
                {notYetBilled > 0 && (
                  <span className="psb-not-due">Not yet billed {fmt(notYetBilled)}</span>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {refundDue > 0 && (
        <div className="psb-refund-warning">
          Credits exceed the remaining unpaid balance — refund due to client: {fmt(refundDue)}
        </div>
      )}

      {hasChangeOrders && (
        <div className="psb-summary">
          <div className="psb-summary-line">
            <span>Original contract</span>
            <span>{fmt(baseTotal)}</span>
          </div>
          <div className="psb-summary-line">
            <span>Change orders</span>
            <span>{coTotal >= 0 ? '+' : '−'}{fmt(Math.abs(coTotal))}</span>
          </div>
          <div className="psb-summary-line psb-summary-total">
            <span>Current contract value</span>
            <span>{fmt(currentContractValue)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentScheduleBreakdown;
